<template>
  <div class="po-summary-page">
    <section class="summary-hero">
      <div>
        <span class="summary-kicker">Purchase Intelligence</span>
        <h1>Purchase Order Summary</h1>
        <p>Monitor branch purchase volume, category spending, and drill into chart or detail views.</p>
      </div>

      <div class="summary-hero__actions">
        <b-button
          variant="light"
          class="summary-refresh"
          :disabled="isFetching"
          @click="refetchData"
        >
          <b-spinner
            v-if="isFetching"
            small
            class="mr-50"
          />
          <feather-icon
            v-else
            icon="RefreshCwIcon"
            size="16"
            class="mr-50"
          />
          Refresh
        </b-button>
      </div>
    </section>

    <b-row class="summary-kpis">
      <b-col
        cols="12"
        md="4"
      >
        <div class="summary-kpi">
          <span class="summary-kpi__icon summary-kpi__icon--teal">
            <feather-icon icon="ShoppingBagIcon" />
          </span>
          <div>
            <small>Total PO Count</small>
            <strong>{{ totalCount }}</strong>
          </div>
        </div>
      </b-col>
      <b-col
        cols="12"
        md="4"
      >
        <div class="summary-kpi">
          <span class="summary-kpi__icon summary-kpi__icon--blue">
            <feather-icon icon="DollarSignIcon" />
          </span>
          <div>
            <small>Total Value</small>
            <strong>{{ formatCurrency(totalSum) }}</strong>
          </div>
        </div>
      </b-col>
      <b-col
        cols="12"
        md="4"
      >
        <div class="summary-kpi">
          <span class="summary-kpi__icon summary-kpi__icon--slate">
            <feather-icon icon="GitBranchIcon" />
          </span>
          <div>
            <small>Branches</small>
            <strong>{{ totalPurchaseOrderSummaryReceives }}</strong>
          </div>
        </div>
      </b-col>
    </b-row>

    <b-card
      no-body
      class="summary-card"
    >
      <div class="summary-toolbar">
        <div class="summary-toolbar__filters">
          <div class="summary-filter">
            <label for="start-datepicker">Start Date</label>
            <b-form-datepicker
              id="start-datepicker"
              v-model="startDateFilter"
              reset-button
              today-button
            />
          </div>
          <div class="summary-filter">
            <label for="end-datepicker">End Date</label>
            <b-form-datepicker
              id="end-datepicker"
              v-model="endDateFilter"
              reset-button
              today-button
            />
          </div>
        </div>

        <div class="summary-toolbar__entries">
          <span>Rows</span>
          <v-select
            v-model="perPage"
            :options="perPageOptions"
            :clearable="false"
            class="per-page-selector"
          />
        </div>
      </div>

      <div class="summary-table-wrap">
        <div
          v-if="isFetching"
          class="summary-loading"
        >
          <div class="summary-loader">
            <span />
            <span />
            <span />
          </div>
          <strong>Fetching purchase data</strong>
          <small>Preparing branch totals and category values...</small>
        </div>

        <b-table
          ref="refPurchaseOrderReceiveWarehouseTable"
          class="summary-table"
          :items="fetchPurchaseOrderSummaryReceives"
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty
          empty-text="No purchase order summary found"
          :sort-desc.sync="isSortDirDesc"
          :busy="isFetching"
        >
          <template #table-busy>
            <div class="text-center text-muted py-3">
              Loading purchase order summary...
            </div>
          </template>

          <template #cell(No)="data">
            <span class="summary-row-no">
              {{ data.index + 1 + (currentPage - 1) * perPage }}
            </span>
          </template>

          <template #cell(Code)="data">
            <span class="branch-code">{{ data.item.branch_code }}</span>
          </template>

          <template #cell(Description)="data">
            <div class="branch-name">
              <strong>{{ data.item.branch_name }}</strong>
              <small>{{ data.item.branch_code }}</small>
            </div>
          </template>

          <template #cell(CountSupplies)="data">
            <span class="count-pill">{{ data.item.count_supplies }}</span>
          </template>

          <template #cell(TotalSupplies)="data">
            <span class="money-cell">{{ formatCurrency(data.item.total_supplies) }}</span>
          </template>

          <template #cell(CountFGS)="data">
            <span class="count-pill">{{ data.item.count_fgs }}</span>
          </template>

          <template #cell(TotalFGS)="data">
            <span class="money-cell">{{ formatCurrency(data.item.total_fgs) }}</span>
          </template>

          <template #cell(CountAssets)="data">
            <span class="count-pill">{{ data.item.count_assets }}</span>
          </template>

          <template #cell(TotalAssets)="data">
            <span class="money-cell">{{ formatCurrency(data.item.total_assets) }}</span>
          </template>

          <template #cell(CountAll)="data">
            <span class="count-pill count-pill--strong">{{ data.item.count_all }}</span>
          </template>

          <template #cell(TotalAll)="data">
            <span class="money-cell money-cell--strong">{{ formatCurrency(data.item.total_all) }}</span>
          </template>

          <template #cell(Action)="data">
            <div class="summary-actions">
              <b-button
                v-b-tooltip.hover
                title="Open chart"
                variant="flat-primary"
                class="summary-action-btn"
                @click="showChart(data.item)"
              >
                <feather-icon
                  icon="BarChart2Icon"
                  size="16"
                />
              </b-button>
              <b-button
                v-b-tooltip.hover
                title="Open details"
                variant="flat-secondary"
                class="summary-action-btn"
                @click="showDetail(data.item)"
              >
                <feather-icon
                  icon="FileTextIcon"
                  size="16"
                />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>

      <div class="summary-footer">
        <span class="text-muted">
          Showing {{ dataMeta.from }} to {{ dataMeta.to }} of {{ dataMeta.of }} entries
        </span>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalPurchaseOrderSummaryReceives || (currentPage * perPage)"
          :per-page="perPage"
          first-number
          last-number
          class="mb-0"
          prev-class="prev-item"
          next-class="next-item"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BButton,
  BCard,
  BCol,
  BFormDatepicker,
  BPagination,
  BRow,
  BSpinner,
  BTable,
  VBTooltip,
} from 'bootstrap-vue'
import { onUnmounted } from '@vue/composition-api'
import store from '@/store'
import vSelect from 'vue-select'
import listPagePersistence from '@/mixins/listPagePersistence'
import PurchaseOrderSummary from './Summary'

export default {
  components: {
    BButton,
    BCard,
    BCol,
    BFormDatepicker,
    BPagination,
    BRow,
    BSpinner,
    BTable,
    vSelect,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [listPagePersistence(['currentPage'])],
  setup() {
    const BRANCH_APP_STORE_MODULE_NAME = 'app-purchase-order'

    onUnmounted(() => {
      if (store.hasModule(BRANCH_APP_STORE_MODULE_NAME)) store.unregisterModule(BRANCH_APP_STORE_MODULE_NAME)
    })

    return {
      ...PurchaseOrderSummary(),
    }
  },
  methods: {
    formatCurrency(value) {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: 'IDR',
        maximumFractionDigits: 0,
      }).format(Number(value || 0))
    },
    showChart(item) {
      localStorage.setItem('SummaryData', JSON.stringify(item))
      this.$router.push({ name: 'apps-procurement-purchase-order-list-summary-chart' })
    },
    showDetail(item) {
      localStorage.setItem('SummaryData', JSON.stringify(item))
      this.$router.push({ name: 'apps-procurement-purchase-order-list-summary-details' })
    },
  },
}
</script>

<style lang="scss" scoped>
.po-summary-page {
  .summary-hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.25rem;
    padding: 1.5rem;
    color: #fff;
    border-radius: 8px;
    background:
      linear-gradient(135deg, rgba(31, 42, 68, 0.96) 0%, rgba(37, 95, 133, 0.94) 54%, rgba(43, 138, 126, 0.94) 100%),
      url('~@/assets/images/banner/banner-12.jpg') center/cover;
    box-shadow: 0 18px 40px rgba(31, 42, 68, 0.18);

    h1 {
      margin-bottom: 0.35rem;
      color: #fff;
      font-size: 1.75rem;
      font-weight: 700;
      letter-spacing: 0;
    }

    p {
      max-width: 650px;
      margin-bottom: 0;
      color: rgba(255, 255, 255, 0.78);
    }
  }

  .summary-kicker {
    display: block;
    margin-bottom: 0.25rem;
    color: rgba(255, 255, 255, 0.72);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  .summary-refresh {
    display: inline-flex;
    align-items: center;
    border: 0;
    border-radius: 8px;
    color: #1f2a44;
    font-weight: 700;
  }

  .summary-kpis {
    margin-bottom: 1.25rem;
  }

  .summary-kpi {
    display: flex;
    align-items: center;
    gap: 0.9rem;
    height: 100%;
    padding: 1rem;
    border: 1px solid #ebe9f1;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(34, 41, 47, 0.06);

    small,
    strong {
      display: block;
    }

    small {
      color: #6e6b7b;
      font-weight: 700;
    }

    strong {
      color: #2f2b43;
      font-size: 1.35rem;
      line-height: 1.2;
    }
  }

  .summary-kpi__icon {
    display: grid;
    width: 44px;
    height: 44px;
    place-items: center;
    border-radius: 8px;

    &--teal {
      color: #2b8a7e;
      background: rgba(43, 138, 126, 0.12);
    }

    &--blue {
      color: #255f85;
      background: rgba(37, 95, 133, 0.12);
    }

    &--slate {
      color: #1f2a44;
      background: rgba(31, 42, 68, 0.1);
    }
  }

  .summary-card {
    border: 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 12px 34px rgba(34, 41, 47, 0.08);
  }

  .summary-toolbar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #ebe9f1;
    background: #fff;
  }

  .summary-toolbar__filters {
    display: grid;
    grid-template-columns: repeat(2, minmax(210px, 1fr));
    gap: 0.85rem;
  }

  .summary-filter label,
  .summary-toolbar__entries span {
    display: block;
    margin-bottom: 0.35rem;
    color: #5e5873;
    font-size: 0.78rem;
    font-weight: 700;
  }

  .summary-toolbar__entries {
    width: 118px;
  }

  .per-page-selector {
    width: 100%;
  }

  .summary-table-wrap {
    position: relative;
    min-height: 320px;
  }

  .summary-loading {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: grid;
    gap: 0.45rem;
    place-content: center;
    text-align: center;
    color: #5e5873;
    background: rgba(255, 255, 255, 0.86);
    backdrop-filter: blur(3px);

    small {
      color: #8a849d;
    }
  }

  .summary-loader {
    display: inline-flex;
    justify-content: center;
    gap: 0.35rem;
    margin-bottom: 0.35rem;

    span {
      width: 9px;
      height: 9px;
      border-radius: 50%;
      background: #2b8a7e;
      animation: summaryPulse 0.9s ease-in-out infinite;

      &:nth-child(2) {
        animation-delay: 0.12s;
      }

      &:nth-child(3) {
        animation-delay: 0.24s;
      }
    }
  }

  .summary-table {
    margin-bottom: 0;

    ::v-deep thead th {
      color: #5e5873;
      border-top: 0;
      background: #f8f8fb;
      font-size: 0.76rem;
      text-transform: uppercase;
    }

    ::v-deep tbody td {
      vertical-align: middle;
    }
  }

  .summary-row-no,
  .count-pill {
    display: inline-grid;
    min-width: 30px;
    min-height: 28px;
    place-items: center;
    padding: 0 0.45rem;
    border-radius: 8px;
    background: #f3f2f7;
    color: #5e5873;
    font-weight: 700;
  }

  .count-pill--strong {
    color: #fff;
    background: #2b8a7e;
  }

  .branch-code {
    color: #255f85;
    font-weight: 700;
  }

  .branch-name {
    display: grid;

    strong {
      color: #3f3b57;
    }

    small {
      color: #9a96aa;
      font-size: 0.76rem;
    }
  }

  .money-cell {
    display: block;
    color: #3f3b57;
    font-weight: 700;
    text-align: right;
    white-space: nowrap;
  }

  .money-cell--strong {
    color: #2b8a7e;
  }

  .summary-actions {
    display: flex;
    gap: 0.35rem;
    justify-content: flex-end;
  }

  .summary-action-btn {
    display: inline-grid;
    width: 34px;
    height: 34px;
    place-items: center;
    padding: 0;
    border-radius: 8px;
  }

  .summary-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.25rem;
    border-top: 1px solid #ebe9f1;
    background: #fff;
  }
}

@keyframes summaryPulse {
  0%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }

  50% {
    opacity: 1;
    transform: translateY(-5px);
  }
}

@media (max-width: 767.98px) {
  .po-summary-page {
    .summary-hero,
    .summary-toolbar,
    .summary-footer {
      flex-direction: column;
      align-items: stretch;
    }

    .summary-toolbar__filters {
      grid-template-columns: 1fr;
    }

    .summary-toolbar__entries {
      width: 100%;
    }
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
